import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

// Persist active role in localStorage so it survives page reloads
const ACTIVE_ROLE_KEY = 'agent_active_role';

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(localStorage.getItem('agent_token'));
    const [agentInfo, setAgentInfo] = useState(null);
    const [agentRoles, setAgentRoles] = useState([]);
    const [activeRole, setActiveRoleState] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            // Verify token/get agent profile (includes roles now)
            fetch('/api/agents/me', {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(res => {
                    if (res.ok) return res.json();
                    // Only treat a real 401/403 from our own API as an invalid session;
                    // anything else (5xx, gateway errors) must not force a logout.
                    const authError = new Error('Unauthorized');
                    authError.isAuthError = res.status === 401 || res.status === 403;
                    throw authError;
                })
                .then(data => {
                    setAgentInfo(data);
                    const roles = data.roles || [];
                    setAgentRoles(roles);

                    // Restore persisted active role if still valid
                    try {
                        const stored = JSON.parse(localStorage.getItem(ACTIVE_ROLE_KEY));
                        const stillValid = stored && roles.some(r => r.id === stored.id);
                        setActiveRoleState(stillValid ? stored : (roles[0] || null));
                    } catch {
                        setActiveRoleState(roles[0] || null);
                    }

                    setLoading(false);
                })
                .catch(err => {
                    // Network errors (offline, backend restart, timeouts) should not
                    // wipe out a still-valid session; only a confirmed 401/403 does.
                    if (err.isAuthError) {
                        logout();
                    }
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, [token]);

    const setActiveRole = (role) => {
        setActiveRoleState(role);
        if (role) {
            localStorage.setItem(ACTIVE_ROLE_KEY, JSON.stringify(role));
        } else {
            localStorage.removeItem(ACTIVE_ROLE_KEY);
        }
    };

    const login = (newToken) => {
        localStorage.setItem('agent_token', newToken);
        setToken(newToken);
    };

    const logout = React.useCallback(() => {
        localStorage.removeItem('agent_token');
        localStorage.removeItem(ACTIVE_ROLE_KEY);
        setToken(null);
        setAgentInfo(null);
        setAgentRoles([]);
        setActiveRoleState(null);
    }, []);

    const authFetch = React.useCallback(async (url, options = {}) => {
        const headers = {
            ...options.headers,
            'Authorization': `Bearer ${token}`
        };

        const response = await fetch(url, { ...options, headers });

        // Only logout if 401 comes from our own auth endpoints, not external APIs
        // or infra errors (proxies/CDNs returning a bare 401/HTML on outage).
        if (response.status === 401) {
            try {
                const data = await response.clone().json();
                // Don't logout if it's an external API error
                if (!data.external_error) {
                    logout();
                    throw new Error('Session expired');
                }
            } catch (e) {
                if (e.message === 'Session expired') throw e;
                // Body wasn't JSON (e.g. gateway/error page): can't confirm this is
                // really our auth middleware rejecting the token, so keep the session.
            }
        }

        return response;
    }, [token, logout]);

    return (
        <AuthContext.Provider value={{ token, agentInfo, agentRoles, activeRole, setActiveRole, login, logout, authFetch, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
